import { useTranslation } from 'react-i18next'

import type { DayOfWeek } from '@/constants/days-of-week'
import {
  useColumns,
  type RouteApiWithColumnsAndPagination
} from '@/hooks/useColumns'
import useDaysOfWeek from '@/hooks/useDaysOfWeek'
import { useScreenResolution } from '@/hooks/useScreenResolution'

import AvailabilitySlot from '../components/AvailabilitySlot/AvailabilitySlot'
import styles from '../components/AvailabilityTable/AvailabilityTable.module.scss'
import type { AvailabilityList } from '../queries/useAvailabilityList'

type UseAvailabilityManagementColumnsProps = {
  day: DayOfWeek
  routeApi: RouteApiWithColumnsAndPagination
}

const useAvailabilityManagementColumns = ({
  day,
  routeApi
}: UseAvailabilityManagementColumnsProps) => {
  const { t } = useTranslation('availabilityManagement')
  const { isMobile } = useScreenResolution()
  const daysOfWeek = useDaysOfWeek()

  return useColumns<AvailabilityList>(
    isMobile && day
      ? [
          {
            key: 'user',
            cellDataTestId: 'username',
            dataIndex: 'username',
            label: t('label.user')
          },
          {
            key: day,
            cellDataTestId: day,
            render: (value: AvailabilityList) => (
              <AvailabilitySlots slots={value[day]} />
            )
          }
        ]
      : [
          {
            key: 'user',
            cellDataTestId: 'username',
            dataIndex: 'username',
            width: 160,
            label: t('label.user')
          },
          ...daysOfWeek.map(({ value, label }) => ({
            key: value,
            label,
            cellDataTestId: value,
            width: 134,
            render: (availabilityRow: AvailabilityList) => (
              <AvailabilitySlots slots={availabilityRow[value]} />
            )
          }))
        ],
    { hideNumberColumnHeader: true, routeApi }
  )
}

type AvailabilitySlotsProps = {
  slots: {
    id: string
    isOptional: boolean
    startTime: string
    endTime: string
  }[]
}

const AvailabilitySlots = ({ slots }: AvailabilitySlotsProps) => (
  <div className={styles.slotsContainer}>
    {slots.length ? (
      slots.map(slot => (
        <AvailabilitySlot
          key={slot.id}
          variant={slot.isOptional ? 'optional' : 'available'}
          startTime={slot.startTime}
          endTime={slot.endTime}
        />
      ))
    ) : (
      <AvailabilitySlot variant="unavailable" />
    )}
  </div>
)

export default useAvailabilityManagementColumns
